// Try to import from vuex first, but fallback to our shim if that fails
// This prevents the "Module is not exported from vuex" error in Storybook
import type { Module } from 'vuex'
import type { ApplicationState } from './types'
import type { RootState } from '@/store/modules/types'
import { clone } from '@/utils/helpers'

// Use type import to avoid runtime errors

export const initialApplicationState = (): ApplicationState => ({
  applicationTime: 0,
  history: { page: '', position: null, prev: null },
})

interface HistoryAddPayload {
  page: string
  position: number | null
}

interface HistoryResetPayload {
  page?: string
  position?: number | null
}

const findPreviousHistoryEntry = (
  history: ApplicationState['history'] | null,
  page: string,
  position: number | null,
): ApplicationState['history'] | null => {
  let current = history

  while (current) {
    if (current.page === page && (position === null || current.position === null || current.position === position)) {
      return current
    }

    current = current.prev
  }

  return null
}

const applicationModule: Module<ApplicationState, RootState> = {
  namespaced: true,
  state: initialApplicationState,
  mutations: {
    SET_APPLICATION_TIME(state: ApplicationState, newApplicationTime: number) {
      state.applicationTime = newApplicationTime
    },
    // Replaces history with a new ApplicationHistory object, placing the old object into 'prev'
    HISTORY_ADD(state: ApplicationState, { page, position }: HistoryAddPayload) {
      if (state.history.page === page && (position === null || state.history.position === position)) {
        return
      }

      const isBrowserBack = position !== null && state.history.position !== null && position < state.history.position
      if (isBrowserBack) {
        const previousHistoryEntry = findPreviousHistoryEntry(state.history.prev, page, position)
        if (previousHistoryEntry) {
          state.history = clone(previousHistoryEntry)
          return
        }
      }

      const cloneHistory = clone(state.history)
      state.history = {
        page,
        position,
        prev: cloneHistory,
      }
    },
    // We can call reset to clear up memory if the current page does not require a history of items (eg Audience page).
    // Can pass in maybePage to define the current route.
    HISTORY_RESET(state: ApplicationState, payload: string | HistoryResetPayload | undefined) {
      const page = typeof payload === 'string' ? payload : payload?.page
      const position = typeof payload === 'string' ? null : payload?.position ?? null

      state.history = { page: page || '', position, prev: null }
    },
    // Replaces history with the value of history.prev, or the default ApplicationHistory object
    HISTORY_POP_LAST(state: ApplicationState) {
      const cloneHistory = clone(state.history)
      state.history = cloneHistory?.prev ? cloneHistory.prev : { page: '', position: null, prev: null }
    },
  },
  getters: {
    // Fetch the previous page path stored in application history, falling back to fallbackPath
    historicalPreviousPagePath(state: ApplicationState) {
      return (fallbackPath: string) => state.history.prev?.page || fallbackPath
    },
    // Fetch the current page path stored in application history, falling back to fallbackPath. Some pages do not get added
    // to history (eg edit/create pages), so we want to fetch the canonically 'current' page when navigating back.
    historicalCurrentPagePath(state: ApplicationState) {
      return (fallbackPath: string) => state.history.page.length ? state.history.page : fallbackPath
    },
  },
}

export default applicationModule
